import { showErrorToast, showWriteLockedToast } from '@/components/Notifications';
import piniaStore from '@/pinia.store';
import useUsersStore from '@/stores/useUsersStore';
import {
  TError400Details,
  TError401Details,
  TError403Details,
  TError404Details,
  TError409Details,
  TError429Details,
  THandlerProps,
  HTTP400Error,
  HTTP401Error,
  HTTP403Error,
  HTTP404Error,
  HTTP409Error,
  HTTP429Error,
  ServerSideError,
  THTTPError,
} from './constants';

//

const TRY_AGAIN_MESSAGE = 'Oops, something went wrong! Please try again later.';
const TOO_MANY_LOGIN_ATTEMPTS_MESSAGE = 'Too many login attempts, please try again later';

// Authentication error types that should trigger token refresh
const REFRESH_TOKEN_ERROR_TYPES = [
  'authentication_error.missing_token',
  'authentication_error.invalid_token',
  'authentication_error.invalid_payload',
  'authentication_error.expired_token',
];

// Symbol to mark requests that have already been retried to prevent infinite loops
const RETRY_SYMBOL = Symbol('isRetryRequest');

// Symbol to store the retry response so customFetch can access the correct status/headers
export const RETRY_RESPONSE_SYMBOL = Symbol('retryResponse');

type TFetchArgs = {
  endpoint: string,
  reqInit: RequestInit,
  handlerProps: THandlerProps | undefined,
  [RETRY_RESPONSE_SYMBOL]?: Response,
};

/**
 * Attempts to refresh the JWT token using the refresh token cookie
 * @returns Promise<boolean> - true if refresh was successful, false otherwise
 */
const refreshJWTToken = async (): Promise<boolean> => {
  try {
    const userStore = useUsersStore(piniaStore);
    const refreshToken = userStore.accessToken;

    if (!refreshToken) {
      console.error('No refresh token available in store');
      return false;
    }

    const { postGesdinetJwtRefreshToken } = await import('@/api/generated/auth/auth');
    const response = await postGesdinetJwtRefreshToken({ refresh_token: refreshToken });

    if (response.status === 200 && response.data.refresh_token) {
      userStore.setAccessToken(response.data.refresh_token);

      return true;
    }

    return false;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
};

/**
 * Checks if a 401 error should trigger an automatic token refresh
 */
const shouldRefreshToken = (details: TError401Details, requestInit: RequestInit): boolean => {
  // Don't retry if this request has already been retried
  if ((requestInit as RequestInit & { [RETRY_SYMBOL]?: boolean })[RETRY_SYMBOL]) {
    return false;
  }

  // Don't retry if this is the refresh token endpoint itself
  const url = (requestInit as RequestInit & { url?: string }).url;

  if (url?.includes('/token/refresh')) {
    return false;
  }

  // Check if error type matches one that should trigger refresh
  return REFRESH_TOKEN_ERROR_TYPES.includes(details.type);
};

const showErrorMessage = (message: string, error: THTTPError, handlerProps?: THandlerProps): void => {
  const { skipErrorToast } = handlerProps || {};

  return skipErrorToast && !skipErrorToast(error)
    ? showErrorToast(message)
    : undefined;
};

const APIResponseHandler = async <RESP_T>(
  response: Response,
  fetchArgs: TFetchArgs,
): Promise<RESP_T> => {
  const { handlerProps } = fetchArgs;

  switch (response.status) {
    case 200:
    { const contentType = response.headers.get('Content-Type');
      return contentType?.includes('application/json')
        ? (response.json() as Promise<RESP_T>)
        : (response.blob() as Promise<RESP_T>); }

    case 201:
    case 202:
      return response.json() as Promise<RESP_T>;

    case 204:
      return Promise.resolve() as Promise<RESP_T>;

      // Error messages from the API addressed only for developers
      // All validations should be realized in the frontend
      // All throws leaved be handled via the Sentry

    case 400: {
      const details: TError400Details = await response.json();
      const error = new HTTP400Error(response, details);

      // WRITE_LOCK_ENABLED refusal — shown regardless of handlerProps, since every form
      // would otherwise report it as its own generic failure.
      if (error.isWriteLocked) {
        showWriteLockedToast();

        return Promise.reject(error);
      }

      showErrorMessage(TRY_AGAIN_MESSAGE, error, handlerProps);

      return Promise.reject(error);
    }

    case 401: {
      try {
        const details: TError401Details = await response.json();

        if (shouldRefreshToken(details, fetchArgs.reqInit)) {
          const refreshSuccessful = await refreshJWTToken();

          if (refreshSuccessful) {
            // Mark this request as a retry to prevent infinite loops
            // Ensure credentials: 'include' is set to send the new JWT cookies
            const retryInit: RequestInit & { [RETRY_SYMBOL]?: boolean } = {
              ...fetchArgs.reqInit,
              credentials: 'include',
              [RETRY_SYMBOL]: true,
            };

            try {
              // Retry the original request with refreshed token (cookies will be sent automatically)
              const retryResponse = await fetch(fetchArgs.endpoint, retryInit);

              // Store the retry response so customFetch can access it
              fetchArgs[RETRY_RESPONSE_SYMBOL] = retryResponse;

              // Create new fetchArgs for the retry to avoid any state pollution
              const retryFetchArgs = {
                endpoint: fetchArgs.endpoint,
                reqInit: retryInit,
                handlerProps: fetchArgs.handlerProps,
              };

              // Recursively handle the retry response and return the result
              // This will properly process the response and return it to the original caller
              return await APIResponseHandler<RESP_T>(retryResponse, retryFetchArgs);
            } catch (retryError) {
              // If the retry fails, throw the error from the retry, not the original 401
              // This ensures the caller receives the correct error (e.g., 409) from the retry
              console.error('Retry request failed after token refresh:', retryError);
              return Promise.reject(retryError) as never;
            }
          }
        }

        // If we reach here, either refresh wasn't needed, failed, or this is already a retry
        const error = new HTTP401Error(response, details);
        showErrorMessage(TRY_AGAIN_MESSAGE, error, handlerProps);

        return Promise.reject(error) as never;
      } catch (error) {
        // If this is already an HTTP error from the retry, re-throw it as-is
        if (error instanceof HTTP400Error
          || error instanceof HTTP401Error
          || error instanceof HTTP403Error
          || error instanceof HTTP404Error
          || error instanceof HTTP409Error
          || error instanceof HTTP429Error
          || error instanceof ServerSideError) {
          throw error;
        }

        // If JSON parsing fails (e.g., CORS error), create error without details
        const authError = new HTTP401Error(response, {
          title: 'Unauthorized',
          type: 'authentication_error',
          statusCode: 401,
          details: [],
        });
        showErrorMessage(TRY_AGAIN_MESSAGE, authError, handlerProps);

        return Promise.reject(authError) as never;
      }
    }

    case 403: {
      const details: TError403Details = await response.json();
      const error = new HTTP403Error(response, details);
      showErrorMessage(TRY_AGAIN_MESSAGE, error, handlerProps);

      return Promise.reject(error) as never;
    }

    case 404: {
      const details: TError404Details = await response.json();
      const error = new HTTP404Error(response, details);
      showErrorMessage(TRY_AGAIN_MESSAGE, error, handlerProps);

      return Promise.reject(error) as never;
    }

    case 409: {
      const details: TError409Details = await response.json();
      const error = new HTTP409Error(response, details);
      showErrorMessage(TRY_AGAIN_MESSAGE, error, handlerProps);

      return Promise.reject(error) as never;
    }

    case 429: {
      const details: TError429Details = await response.json();
      const error = new HTTP429Error(response, details);
      showErrorMessage(TOO_MANY_LOGIN_ATTEMPTS_MESSAGE, error, handlerProps);

      return Promise.reject(error) as never;
    }

    // without the body

    case 500:
    case 502:
    case 503:
    case 504: {
      const error = new ServerSideError(response);
      showErrorMessage(TRY_AGAIN_MESSAGE, error, handlerProps);

      return Promise.reject(error) as never;
    }

    default:
      showErrorToast(TRY_AGAIN_MESSAGE);

      return Promise.reject(new TypeError(`API Error: Unexpected error ${response.status}`)) as never;
  }
};

export default APIResponseHandler;
